import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';

import { useEventDocuments } from '@api/bff/eventDocuments';

import type { EventDocumentViewModel } from '@api/bff/openapi/models';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];

const getMimeType = (fileName: string): string => {
  const ext = fileName.toLowerCase().split('.').pop();
  switch (ext) {
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    default:
      return 'image/jpeg';
  }
};

const isImageFile = (doc: EventDocumentViewModel): boolean => {
  if (!doc.fileContent) return false;
  if (!doc.fileName) return true;
  const ext = doc.fileName.toLowerCase();
  return IMAGE_EXTENSIONS.some(imgExt => ext.endsWith(imgExt));
};

type Props = {
  eventNum: number | null | undefined;
};

const HistoryPhotos: React.FC<Props> = ({ eventNum }) => {
  const { t } = useTranslation('history');
  const theme = useTheme();
  const [selectedPhoto, setSelectedPhoto] =
    useState<EventDocumentViewModel | null>(null);

  const { data: eventDocuments, isLoading } = useEventDocuments(eventNum);

  const photos = useMemo(
    () => eventDocuments?.filter(isImageFile) ?? [],
    [eventDocuments]
  );

  if (!eventNum) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('photos.title')}
        </Typography>
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <PhotoLibraryOutlinedIcon
            sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {t('photos.unavailable')}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('photos.title')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </Paper>
    );
  }

  if (photos.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('photos.title')}
        </Typography>
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <PhotoLibraryOutlinedIcon
            sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {t('photos.noPhotos')}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('photos.title')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 2
          }}
        >
          {photos.map((photo, index) => {
            const dataUrl = `data:${getMimeType(photo.fileName ?? '')};base64,${photo.fileContent}`;

            return (
              <Box
                key={photo.documentNumber ?? index}
                onClick={() => setSelectedPhoto(photo)}
                sx={{
                  position: 'relative',
                  paddingTop: '100%',
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <Box
                  component="img"
                  src={dataUrl}
                  alt={photo.description || photo.fileName || ''}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Dialog
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        {selectedPhoto && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setSelectedPhoto(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
              }}
            >
              <Box
                component="img"
                src={`data:${getMimeType(selectedPhoto.fileName ?? '')};base64,${selectedPhoto.fileContent}`}
                alt={selectedPhoto.description || selectedPhoto.fileName || ''}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: 1
                }}
              />
            </Box>
            {(selectedPhoto.description || selectedPhoto.comment) && (
              <Box sx={{ px: 2, pb: 2 }}>
                {selectedPhoto.description && (
                  <Typography variant="body2">
                    {selectedPhoto.description}
                  </Typography>
                )}
                {selectedPhoto.comment && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedPhoto.comment}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default HistoryPhotos;
